import { HTTPClient } from "../HTTPClient"

export class LineNotify {

  /** @type {HTTPClient} */
  http

  /**
   * LINE Notify Service
   * 
   * @param {HTTPClient} http 
   * @param {String} backend 
   */
  constructor(http, backend) {
    this.http = http
    this.url = `${backend}/line-notify`    
  }

  /**
   * @typedef Result
   * @type {Object}
   * @property {Number} code - Result Code
   * @property {String} message - Result Message
   */

  /**
   * @typedef ListResult
   * @type {Object}
   * @property {Number} total - Total Count
   * @property {Array} items - Data List
   */

  /**
   * Fetch Notice List
   * 
   * @param {String} search - Search Text
   * @param {Number} page - Fetch Page
   * @param {Number} limit - Page Limitation
   * @returns {Promise<Result & ListResult>} Notice List
   */
  async noticeList (search, page, limit) {

    // Set Payload Props
    search = search || ''
    page = parseInt(page) || 1
    limit = parseInt(limit) || 10

    // Set Request & Payload
    const api = `${this.url}/list-notice`
    const params = {
      search, page, limit
    }

    // Request Data
    const response = await this.http.request(
      'get', api, { params }
    )

    // Default Result
    let code = 50000
    let message = this.messages(code)
    let data = {
      total: 0, items: []
    }

    // Validate Result
    if (response.status == 200) {

      // Response Data
      const { result } = response.data

      // Result Data
      code = result.code
      data = result.data
      
      // Result Message
      message = this.messages(code)
    }

    return {
      code, message, ...data
    }
  }

  /**
   * Fetch Admin List
   * 
   * @param {String} search - Search Text
   * @param {Number} page - Fetch Page
   * @param {Number} limit - Page Limitation
   * @returns {Promise<Result & ListResult>} Admin List
   */
  async adminList (search, page, limit) {

    // Set Payload Props
    search = search || ''
    page = parseInt(page) || 1
    limit = parseInt(limit) || 10

    // Set Request & Payload
    const api = `${this.url}/list-admin`
    const params = {
      search, page, limit
    }

    // Request Data
    const response = await this.http.request(
      'get', api, { params }
    )

    // Default Result
    let code = 50000
    let message = this.messages(code)
    let data = {
      total: 0, items: []
    }

    // Validate Result
    if (response.status == 200) {

      // Response Data
      const { result } = response.data

      // Result Data
      code = result.code
      data = result.data

      // Result Message
      message = this.messages(code)
    }

    return {
      code, message, ...data
    }
  }

  /**
   * Fetch Project List
   * 
   * @param {String} search - Search Text
   * @param {Number} page - Fetch Page
   * @param {Number} limit - Page Limitation
   * @returns {Promise<Result & ListResult>} Project List
   */
  async projectList (search, page, limit) {

    // Set Payload Props
    search = search || ''
    page = parseInt(page) || 1
    limit = parseInt(limit) || 10

    // Request API
    const api = `${this.url}/list-project`

    // Request Payload
    const params = {
      search, page, limit
    }

    // Request Data
    const response = await this.http.request(
      'get', api, { params }
    )

    // Default Result
    let code = 50000
    let message = this.messages(code)
    let data = {
      total: 0, items: []
    }

    // Validate Result
    if (response.status == 200) {

      // Response Data
      const { result } = response.data

      // Result Data
      code = result.code
      data = result.data
      
      // Result Message
      message = this.messages(code)
    }

    return {
      code, message, ...data
    }
  }

  /**
   * @typedef CreateUser
   * @type {Object}
   * @property {String} lineId - LINE UID
   * @property {String} ruejaiId - User UID
   * @property {String} email - User Email
   * @property {String} firstName - First Name
   * @property {String} lastName - Last Name
   * @property {String} roleName - User Role Name
   * @property {String[]} projects - Notice Project IDs
   */

  /**
   * @typedef CreateResult
   * @type {Object}
   * @property {Number} id
   */

  /**
   * Create LINE Notify User
   * 
   * @param {CreateUser} params - LINE User Data
   * @returns {Promise<Result & CreateResult>} - Created Result
   */
  async addUser (params) {
    
    // Request API
    const api = `${this.url}/add-user`

    // Request Payload
    const payload = { ...params }

    // Request Data
    const response = await this.http.request(
      'post', api, { data: payload }
    )

    // Default Result
    let code = 50000
    let message = this.messages(code)
    let data = 0

    // Validate Result
    if (response.status == 200) {

      // Response Data
      const { result } = response.data

      // Result Data
      code = result.code
      data = result.data

      // Result Message
      message = this.messages(code)
    }

    return {
      code, message, data
    }
  }


  /**
   * @typedef NoticeResult
   * @type {Object}
   * @property {Number} id - Notice ID
   * @property {String} projectId - Project ID
   */

  /**
   * @typedef AddNoticeResult
   * @type {Object}
   * @property {NoticeResult[]} data
   */

  /**
   * Add Notices
   * 
   * @param {Number} id 
   * @returns {Promise<Result & AddNoticeResult>}
   */
  async addNotice (ruejaiId, project) {
    
    // Request API
    const api = `${this.url}/add-notice`

    // Request Payload
    const payload = { ruejaiId, project }

    // Request Data
    const response = await this.http.request(
      'post', api, { data: payload }
    )

    // Default Result
    let code = 50000
    let message = this.messages(code)
    let data = []

    // Validate Result
    if (response.status == 200) {

      // Response Data
      const { result } = response.data

      // Result Data
      code = result.code
      data = result.data

      // Result Message
      message = this.messages(code)
    }

    return {
      code, message, data
    }
  }

  /**
   * @typedef RemoveResult
   * @type {Object}
   * @property {Number} affected - delete affected rows
   */

  /**
   * Remove LINE User by ID
   * 
   * @param {String} id - Ruejai ID (Admin ID)
   * @returns {Promise<Result & RemoveResult>} Soft Deleted Result
   */
  async removeUser (id) {

    // Request API
    const api = `${this.url}/remove-user/${id}`

    // Request Data
    const response = await this.http.request(
      'delete', api
    )

    // Default Result
    let code = 50000
    let message = this.messages(code)
    let data = 0

    // Validate Result
    if (response.status == 200) {

      // Response Data
      const { result } = response.data

      // Result Data
      code = result.code
      data = result.data

      // Result Message
      message = this.messages(code)
    }

    return {
      code, message, data
    }
  }

  /**
   * Remove Notice by ID
   * 
   * @param {Number} id - Notice ID
   * @returns {Promise<Result & RemoveResult>} Soft Deleted Result
   */
  async removeNotice (id) {

    // Request API
    const api = `${this.url}/remove-notice/${id}`

    // Request Data
    const response = await this.http.request(
      'delete', api
    )

    // Default Result
    let code = 50000
    let message = this.messages(code)
    let data = 0

    // Validate Result
    if (response.status == 200) {

      // Response Data
      const { result } = response.data

      // Result Data
      code = result.code
      data = result.data

      // Result Message
      message = this.messages(code)
    }

    return {
      code, message, data
    }
  }
  
  /**
   * Set Description
   * 
   * @param {Number} code 
   * @returns {String} Result Description
   */
  messages (code) {

    // Descriptions
    const messages = {
      20000: 'เรียบร้อย',
      40000: 'โปรดกรอกข้อมูลให้ครบถ้วน',
      40400: 'ไม่พบข้อมูล',
      40900: 'มีข้อมูลอยู่แล้ว',
      50000: 'เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง'
    }
    
    // Message
    return messages[code || 50000]
  }
}


// WEBPACK FOOTER //
// ./src/clients/BaanRueJaiClient/line-notify.js